/* ═══════════════════════════════════════════════════════════════
   ADAPTADOR LEGACY — adminPrograms
   Lee del motor canónico programsMaster.ts y exporta en el formato
   antiguo esperado por la página /admin/programs.
   ═══════════════════════════════════════════════════════════════ */

import { canonicalPrograms, getProgramById } from './programsMaster';
import { canonicalPersons } from './persons';
import type { CanonicalProgram } from './programsMaster';

/* ─── Tipos legacy (sin cambios) ─── */
export type ProgramCategory = 'musical' | 'noticias' | 'deportes' | 'entretenimiento' | 'entrevistas' | 'cultural' | 'tecnologia' | 'variedades';

export interface UpcomingEpisode {
  id: number;
  title: string;
  description: string;
  date: string;
  guest?: string;
  isSpecial: boolean;
}

export interface ProgramPost {
  id: number;
  title: string;
  excerpt: string;
  imageUrl: string;
  date: string;
  type: 'anuncio' | 'resumen' | 'detras-de-camaras' | 'invitado';
}

export interface Program {
  id: number;
  name: string;
  slug: string;
  hostName: string;
  hostPhotoUrl: string;
  coHosts: string[];
  category: ProgramCategory;
  description: string;
  imageUrl: string;
  schedule: string;
  days: string[];
  isActive: boolean;
  followers: number;
  interactions: number;
  likes: number;
  comments: number;
  shares: number;
  upcomingEpisodes: UpcomingEpisode[];
  recentPosts: ProgramPost[];
  createdAt: string;
}

export const categoryLabels: Record<ProgramCategory, string> = {
  musical: 'Musical',
  noticias: 'Noticias',
  deportes: 'Deportes',
  entretenimiento: 'Entretenimiento',
  entrevistas: 'Entrevistas',
  cultural: 'Cultural',
  tecnologia: 'Tecnología',
  variedades: 'Variedades',
};

export const categoryColors: Record<ProgramCategory, string> = {
  musical: 'bg-violet-100 text-violet-700',
  noticias: 'bg-sky-100 text-sky-700',
  deportes: 'bg-red-100 text-red-700',
  entretenimiento: 'bg-amber-100 text-amber-700',
  entrevistas: 'bg-emerald-100 text-emerald-700',
  cultural: 'bg-pink-100 text-pink-700',
  tecnologia: 'bg-cyan-100 text-cyan-700',
  variedades: 'bg-primary-100 text-primary-700',
};

export const categoryAccentColors: Record<ProgramCategory, string> = {
  musical: 'bg-violet-500',
  noticias: 'bg-sky-500',
  deportes: 'bg-red-500',
  entretenimiento: 'bg-amber-500',
  entrevistas: 'bg-emerald-500',
  cultural: 'bg-pink-500',
  tecnologia: 'bg-cyan-500',
  variedades: 'bg-primary-500',
};

export const postTypeLabels: Record<string, string> = {
  anuncio: 'Anuncio',
  resumen: 'Resumen',
  'detras-de-camaras': 'Detrás de Cámaras',
  invitado: 'Invitado Especial',
};

/* ─── Adaptador: canonical → legacy Program ─── */
function canonicalToProgram(cp: CanonicalProgram, idx: number): Program {
  const hostPerson = cp.hostPersonIds.length > 0
    ? canonicalPersons.find((p) => p.id === cp.hostPersonIds[0])
    : null;
  const hostName = hostPerson?.displayName || '';
  const hostPhoto = hostPerson?.avatar || '';

  return {
    id: idx + 1,
    name: cp.title,
    slug: cp.slug,
    hostName,
    hostPhotoUrl: hostPhoto,
    coHosts: cp.producerPersonIds.map((pid) => {
      const pp = canonicalPersons.find((p) => p.id === pid);
      return pp?.displayName || '';
    }).filter(Boolean),
    category: (cp.category === 'especial' || cp.category === 'evento' || cp.category === 'educacion')
      ? 'variedades' as ProgramCategory
      : cp.category as ProgramCategory,
    description: cp.description,
    imageUrl: cp.coverImage,
    schedule: '',
    days: [],
    isActive: cp.status === 'active',
    followers: cp.followers,
    interactions: cp.interactions.likes + cp.interactions.comments + cp.interactions.shares,
    likes: cp.interactions.likes,
    comments: cp.interactions.comments,
    shares: cp.interactions.shares,
    upcomingEpisodes: cp.upcomingEpisodes,
    recentPosts: [],
    createdAt: cp.createdAt,
  };
}

/* ─── Los 8 programas legacy (del motor canónico) ─── */
const legacyProgramCanonicalIds = [
  'prog-001', // El Mañanero → id 1
  'prog-002', // La Hora Pico → id 2
  'prog-003', // Zona Deportiva → id 3
  'prog-004', // Entrevistas Exclusivas → id 4
  'prog-005', // Beats & Bytes → id 5
  'prog-006', // Top 40 Countdown → id 6
  'prog-007', // Sesiones Acústicas → id 7
  'prog-008', // Noticias al Instante → id 8
];

function buildAdminPrograms(): Program[] {
  return legacyProgramCanonicalIds
    .map((cid, idx) => {
      const cp = canonicalPrograms.find((p) => p.id === cid);
      if (!cp) return null;
      return canonicalToProgram(cp, idx);
    })
    .filter(Boolean) as Program[];
}

export const adminPrograms: Program[] = buildAdminPrograms();

/* ─── Datos de comunidad (sin cambios, usan IDs de programa legacy) ─── */
export const communityFollowers: Record<number, { name: string; username: string; avatarUrl: string; tier: string }[]> = {
  1: [
    { name: 'Daniela Mora', username: '@danimora', avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20portrait%2C%20friendly%20smile%2C%20casual%20style%2C%20warm%20natural%20lighting%2C%20clean%20background%2C%20professional%20headshot&width=120&height=120&seq=community-dani&orientation=squarish', tier: 'Top Fan' },
    { name: 'José Pablo Ruiz', username: '@josepablor', avatarUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20in%20his%2020s%20portrait%2C%20warm%20smile%2C%20casual%20button%20shirt%2C%20studio%20lighting%2C%20clean%20background%2C%20professional%20headshot&width=120&height=120&seq=community-jose&orientation=squarish', tier: 'VIP' },
    { name: 'María Fernanda Solís', username: '@mafesolis', avatarUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20in%20her%2030s%2C%20professional%20friendly%20expression%2C%20natural%20makeup%2C%20warm%20studio%20lighting%2C%20clean%20background%2C%20headshot%20photography&width=120&height=120&seq=community-mafe&orientation=squarish', tier: 'Seguidor' },
  ],
  6: [
    { name: 'Andrea Castillo', username: '@andrecas', avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20with%20curly%20hair%2C%20bright%20smile%2C%20casual%20denim%2C%20warm%20natural%20light%2C%20clean%20background%2C%20editorial%20portrait&width=120&height=120&seq=community-andrea&orientation=squarish', tier: 'Top Fan' },
    { name: 'Luis Fernando Vega', username: '@luisfvega', avatarUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20in%20his%20late%2020s%2C%20friendly%20approachable%20look%2C%20casual%20sweater%2C%20warm%20studio%20lighting%2C%20clean%20portrait%20photography&width=120&height=120&seq=community-luis&orientation=squarish', tier: 'VIP' },
  ],
};

export const programStats: Record<number, { weeklyGrowth: number; avgListenTime: string; peakHour: string; topCountry: string }> = {
  1: { weeklyGrowth: 12.4, avgListenTime: '48 min', peakHour: '07:30', topCountry: 'Costa Rica' },
  2: { weeklyGrowth: 8.7, avgListenTime: '35 min', peakHour: '18:15', topCountry: 'México' },
  3: { weeklyGrowth: -2.1, avgListenTime: '52 min', peakHour: '21:00', topCountry: 'Argentina' },
  4: { weeklyGrowth: 15.2, avgListenTime: '42 min', peakHour: '11:30', topCountry: 'Colombia' },
  5: { weeklyGrowth: 6.3, avgListenTime: '31 min', peakHour: '21:45', topCountry: 'Chile' },
  6: { weeklyGrowth: 18.9, avgListenTime: '40 min', peakHour: '16:00', topCountry: 'Costa Rica' },
  7: { weeklyGrowth: 5.1, avgListenTime: '55 min', peakHour: '19:30', topCountry: 'España' },
  8: { weeklyGrowth: 9.8, avgListenTime: '28 min', peakHour: '12:45', topCountry: 'Costa Rica' },
};